(() => {
  'use strict';
  const endpoint = 'https://bossco-impersonation-evidence.boulvandre-credit-app.workers.dev/upload';
  const allowedTypes = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);

  function initialise(root) {
    if (root.dataset.reportReady) return;
    const form = root.querySelector('form');
    const pictures = root.querySelector('[data-report-pictures]');
    if (!form || !pictures || !window.fetch || !window.FormData || !window.AbortController) return;
    root.dataset.reportReady = 'true';
    const evidence = root.querySelector('[data-report-evidence]');
    const status = root.querySelector('[data-report-status]');
    const list = root.querySelector('[data-report-files]');
    const submit = root.querySelector('[data-report-submit]');
    const retry = root.querySelector('[data-report-retry]');
    const remove = root.querySelector('[data-report-remove]');
    let sequence = 0;
    let controller = null;
    let state = 'idle';

    function setState(next, message) {
      state = next;
      status.textContent = message;
      status.dataset.error = String(next === 'error');
      submit.disabled = next === 'uploading';
      submit.textContent = next === 'uploading' ? 'Uploading pictures…' : 'Send report';
      pictures.setCustomValidity(next === 'uploading' ? 'Please wait for your pictures to finish uploading.' : next === 'error' ? message : '');
      retry.hidden = next !== 'error';
      remove.hidden = !pictures.files.length;
    }

    function showFiles(files) {
      list.replaceChildren();
      files.forEach(file => {
        const item = document.createElement('li');
        item.textContent = file.name;
        list.appendChild(item);
      });
      list.hidden = !files.length;
    }

    async function uploadPictures() {
      const current = ++sequence;
      if (controller) controller.abort();
      evidence.value = '';
      const files = Array.from(pictures.files);
      showFiles(files);
      if (!files.length) return setState('idle', 'No pictures selected. You can send your report without pictures.');
      if (files.length > 3) return setState('error', 'Choose no more than 3 pictures, or remove them to send your report without pictures.');
      if (files.some(file => !file.size || file.size > 5 * 1024 * 1024)) return setState('error', 'Each picture must be no larger than 5 MB and cannot be empty. Choose smaller pictures or remove them.');
      if (files.some(file => !allowedTypes.has(file.type))) return setState('error', 'Choose JPG, PNG, WebP or GIF pictures, or remove them to send your report without pictures.');
      setState('uploading', `Uploading ${files.length === 1 ? 'your picture' : 'your pictures'}… Please wait before sending your report.`);
      controller = new AbortController();
      const activeController = controller;
      const timeout = setTimeout(() => activeController.abort(), 90000);
      try {
        const body = new FormData();
        files.forEach(file => body.append('images', file));
        const response = await fetch(endpoint, { method: 'POST', body, signal: activeController.signal, credentials: 'omit' });
        const result = await response.json();
        if (current !== sequence) return;
        if (!response.ok) throw new Error(result.error || 'Pictures could not be uploaded. Please retry or remove them to send your report without pictures.');
        const validUrls = Array.isArray(result.urls) && result.urls.length === files.length && result.urls.every(value => {
          try {
            const url = new URL(value);
            return url.origin === new URL(endpoint).origin && /^\/evidence\/[0-9a-f-]{73}$/.test(url.pathname) && !url.search && !url.hash;
          } catch { return false; }
        });
        if (!validUrls) throw new Error('Pictures could not be confirmed. Please retry or remove them to send your report without pictures.');
        evidence.value = result.urls.join('\n');
        setState('ready', `${files.length === 1 ? '1 picture is' : `${files.length} pictures are`} ready to include. You can now send your report.`);
      } catch (error) {
        if (current !== sequence) return;
        evidence.value = '';
        const message = error.name === 'AbortError'
          ? 'The upload took too long. Please retry, choose smaller pictures, or remove them to send your report without pictures.'
          : error instanceof TypeError
            ? 'Pictures could not be uploaded. Check your connection, then retry or remove them to send your report without pictures.'
            : error.message;
        setState('error', message);
      } finally {
        clearTimeout(timeout);
        if (current === sequence) controller = null;
      }
    }

    pictures.addEventListener('change', uploadPictures);
    retry.addEventListener('click', uploadPictures);
    remove.addEventListener('click', () => {
      sequence++;
      if (controller) controller.abort();
      controller = null;
      pictures.value = '';
      evidence.value = '';
      showFiles([]);
      setState('idle', 'Pictures removed. You can send your report without pictures.');
    });
    form.addEventListener('submit', event => {
      if (state === 'uploading' || state === 'error') {
        event.preventDefault();
        event.stopImmediatePropagation();
        status.focus();
      }
    }, true);
    root.querySelector('[data-report-uploader]').hidden = false;
    root.querySelector('[data-report-picture-fallback]').hidden = true;
    pictures.disabled = false;
  }

  document.querySelectorAll('[data-bossco-report]').forEach(initialise);
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('[data-bossco-report]').forEach(initialise);
  });
})();
